import { Component, EventEmitter, Input, Output, OnInit, DoCheck, ChangeDetectionStrategy } from "@angular/core";
import { AdminService, BaseComponent, ContentService, UserService, WorkspaceService, ProductService, SimpleProduct, encodeFilenameForHeader } from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { DatePickerComponent } from "../date-picker/date-picker.component";
import { FileUploader } from "ng2-file-upload";
import { interval, Subject } from "rxjs";
import { skip, takeUntil, throttle } from "rxjs/operators";
import { ProductContentService } from "../../../services/product-content.service";
import { ContentData } from "libs/xplat/core/src/lib/models/admin.model";
import { TranslateService } from "@ngx-translate/core";

// Local interface for segmented bar options
interface ContentTypeOption {
  name: string;
  selected: boolean;
  id: string;
}

// Extended interface for component-specific data
interface ExtendedContentData extends Omit<ContentData, "releaseDate"> {
  product_id?: number;
  exists?: boolean;
  releaseDate?: Date | string;
  [key: string]: any;
}

// Local interface for product selection
interface ProductSelection {
  id: number;
  title: string;
  DTYPE?: string;
  imageUrl?: string;
  added?: boolean;
  loading?: boolean;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-add-edit-product-content",
  templateUrl: "add-edit-product-content.component.html",
  standalone: false,
})
export class AddEditProductContentComponent extends BaseComponent implements OnInit, DoCheck {
  // Input/Output properties
  @Input() data: ExtendedContentData = {} as ExtendedContentData;
  @Input() productId: number;
  @Input() productImageOnly: boolean = false;
  @Input() sampleOnly: boolean = false;
  @Input() showProductSelection: boolean = false;
  @Input() selectedProductIds: Array<number | string> = [];
  @Input() isContentPackagesManager: boolean = false;
  @Input() isCreating: boolean = false;
  @Output() completed: EventEmitter<any> = new EventEmitter();

  // Content type selection properties
  public segmentedBarOptions: ContentTypeOption[] = [];
  public selectedContentType: string = "content";

  // File upload properties
  public hasBaseDropZoneOver = false;
  public loading: boolean = false;
  public uploader: FileUploader;
  public uploadingComplete: boolean = false;
  public uploadingFile: any;
  public uploadProgress: number;
  public createdContentPackageItem: any;
  public fileResponse: any;
  public replacingFile: boolean = false;
  public originalStorageLocation: string;
  private _multiProductFileUpload: boolean = false;

  // Product selection properties
  public associatedProductsQuery: string = "";
  public associatedProductResults: ProductSelection[] = [];
  public selectedProducts: ProductSelection[] = [];
  public canSubmitForm: boolean = false;
  private _associatedProductsQueryStream$: Subject<string> = new Subject();
  private _initialSelectedProductIds: Set<number> = new Set();

  constructor(
    userService: UserService,
    public activeModal: NgbActiveModal,
    public adminService: AdminService,
    public appService: AppService,
    public workspaceService: WorkspaceService,
    public contentService: ContentService,
    public productService: ProductService,
    private productContentService: ProductContentService,
    private translate: TranslateService
  ) {
    super(userService);
  }

  ngOnInit(): void {
    this.initializeSegmentedBarOptions();
    this.initializeProductSelection();
    this.initializeContentData();
    this.initializeContentType();
    this.initializeFileUploader();
    this.initializeProductSearch();
  }

  ngDoCheck(): void {
    this.canSubmitForm = this.canSubmit();
  }

  // Initialization methods
  private initializeSegmentedBarOptions(): void {
    this.segmentedBarOptions = [
      { name: this.translate.instant("general.content"), selected: true, id: "content" },
      { name: this.translate.instant("general.firmware"), selected: false, id: "firmware" },
    ];
  }

  private initializeProductSelection(): void {
    setTimeout(() => {
      if (this.showProductSelection && this.selectedProductIds?.length) {
        const ids = this.selectedProductIds.map((x) => parseInt(x as any, 10)).filter((n) => !isNaN(n));
        this._initialSelectedProductIds = new Set(ids);

        this.adminService.getProductInfo(this.selectedProductIds, this.destroy$).subscribe((productInfo) => {
          this.selectedProducts = productInfo;
        });
      }
    });
  }

  private initializeContentData(): void {
    if (!this.data || !this.data.id) {
      this.data.releaseDate = new Date();
    }
    if (this.data.id) {
      this.originalStorageLocation = `${this.data.folder}/${this.data.fileName}`;
    }
    // Normalize legacy beta values (true/"1") to "public", falsy to null.
    if (this.data.beta === true || `${this.data.beta}` === "1") {
      this.data.beta = "public";
    } else if (!this.data.beta) {
      this.data.beta = null;
    }
    this.data.DTYPE = this.data.type === "firmware" ? "Firmware" : "ContentPackage";

    if (this.sampleOnly) {
      this.data.DTYPE = "ContentPackage";
      this.data.type = "audio";
      this.data.folder = this.productId ? `samples/${this.productId}` : "samples";
      this.data.downloadSection = "samples";
      if (!this.data.stringId) this.data.stringId = "sample";
    }
  }

  private initializeContentType(): void {
    if (this.isContentPackagesManager) {
      this.selectedContentType = this.data.type || "content";
      if (this.selectedContentType === "firmware") {
        this.data.downloadSection = "firmware";
      }
      this.updateSegmentedBarOptions();
    }
  }

  private updateSegmentedBarOptions(): void {
    this.segmentedBarOptions.forEach((option) => {
      option.selected = option.id === this.selectedContentType;
    });
  }

  private initializeFileUploader(): void {
    this.uploader = new FileUploader({
      isHTML5: true,
      method: "PUT",
      queueLimit: 1,
      autoUpload: false,
      disableMultipart: true,
      url: "",
      allowedMimeType: this.sampleOnly ? ["audio/mpeg", "audio/mp3"] : undefined,
    });

    this.setupFileUploaderHandlers();
  }

  private setupFileUploaderHandlers(): void {
    this.uploader.onWhenAddingFileFailed = (item, filter) => {
      if (filter?.name === "mimeType") {
        this.appService.alertError("Only .mp3 files are allowed.");
      }
    };

    this.uploader.onAfterAddingFile = (file) => {
      file.withCredentials = false;
      this.uploadingFile = file;
      this.data.contentLength = this.uploadingFile.file.size;
      this.data.fileName = this.uploadingFile.file.name;
      if (this.sampleOnly && !this.data.title) {
        this.data.title = this.uploadingFile.file.name.replace(/\.[^.]+$/, "");
      }
      this.contentService.uploadSession.filesMap[this.uploadingFile.file.name] = { file: this.uploadingFile };
    };

    this.uploader.onProgressAll = (progress: any) => {
      this.uploadProgress = progress || 1;
      if (this.contentService.uploadSession.filesMap[this.uploadingFile.file.name]) {
        this.contentService.uploadSession.filesMap[this.uploadingFile.file.name].progress = progress;
      }
    };

    this.uploader.onCompleteAll = () => {
      this.uploadingComplete = true;
      if (this._multiProductFileUpload) {
        this._multiProductFileUpload = false;
        this.loading = false;
        this.completed.next(this.createdContentPackageItem);
        this.activeModal.close();
      } else {
        this.completed.next(this.createdContentPackageItem);
      }
    };
  }

  private initializeProductSearch(): void {
    if (this.showProductSelection) {
      this._associatedProductsQueryStream$
        .pipe(
          skip(1),
          throttle(() => interval(1000), { leading: true, trailing: true }),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.productAutocomplete();
        });
    }
  }

  // File handling methods
  clearFile(): void {
    this.uploader.clearQueue();
    this.uploadingComplete = false;
    this.uploadingFile = undefined;
    this.fileResponse = undefined;
    this.hasBaseDropZoneOver = false;
  }

  fileOverBase(e: boolean): void {
    this.hasBaseDropZoneOver = e;
  }

  showDatePicker(): void {
    const modalInstance = this.appService.showModal(DatePickerComponent, {
      size: "lg",
      ariaLabelledBy: "modal-title",
    });

    if (this.data.releaseDate) {
      modalInstance.componentInstance.initWithDate = new Date(this.data.releaseDate);
    }

    modalInstance.componentInstance.dateSelected.subscribe((date: Date) => {
      this.data.releaseDate = date;
    });
  }

  // Content type methods
  onContentTypeChange(): void {
    this.data.type = this.selectedContentType;
    this.data.DTYPE = this.selectedContentType === "firmware" ? "Firmware" : "ContentPackage";
    if (this.selectedContentType === "firmware") {
      this.data.downloadSection = "firmware";
    }
  }

  onSegmentedBarSelection(selectedOption: ContentTypeOption): void {
    this.selectedContentType = selectedOption.id;
    this.onContentTypeChange();
  }

  // Product search and selection methods
  productAutocomplete(): void {
    if (this.associatedProductsQuery) {
      this.productService.searchv2("title", this.associatedProductsQuery, 10).subscribe((result: ProductSelection[]) => {
        this.associatedProductResults = result;
        this.associatedProductResults.forEach((prod) => {
          prod.added = this.selectedProducts.some((product) => product.id === prod.id);
        });
      });
    } else {
      this.associatedProductResults = [];
    }
  }

  onProductSearchChange(): void {
    if (this.associatedProductsQuery) {
      this._associatedProductsQueryStream$.next(this.associatedProductsQuery);
    } else {
      this.associatedProductResults = [];
    }
  }

  addProduct(product: ProductSelection): void {
    if (!this.selectedProducts.some((p) => p.id === product.id)) {
      this.selectedProducts.push(product);
      product.added = true;
    }
  }

  removeProduct(product: ProductSelection): void {
    const resultProduct = this.associatedProductResults.find((p) => p.id === product.id);
    this.selectedProducts = this.selectedProducts.filter((p) => p.id !== product.id);
    if (resultProduct) {
      resultProduct.added = false;
    }
  }

  // Validation method
  canSubmit(): boolean {
    // Image-only uploads don't collect content metadata fields in the form.
    if (this.productImageOnly) {
      const hasImageFile = !!this.data.fileName;
      const hasProductTarget = this.selectedProducts.length > 0 || !!this.productId;
      const notProcessing = !this.loading && !this.uploadProgress;
      return hasImageFile && hasProductTarget && notProcessing;
    }

    // Samples-only uploads collect just a title + the mp3 file.
    if (this.sampleOnly) {
      const hasFile = !!this.data.fileName;
      const hasTitle = !!this.data.title;
      const hasProductTarget = this.selectedProducts.length > 0 || !!this.productId;
      const notProcessing = !this.loading && !this.uploadProgress;
      return hasFile && hasTitle && hasProductTarget && notProcessing;
    }

    // Check if required fields are filled
    const hasRequiredFields = this.data.fileName && this.data.folder && this.data.title && this.data.stringId;

    // Check if we have products selected or a productId
    const hasProducts = this.selectedProducts.length > 0 || this.productId;

    // Check if we're not currently loading or uploading
    const notProcessing = !this.loading && !this.uploadProgress;

    return hasRequiredFields && hasProducts && notProcessing;
  }

  // Submission methods
  submit(): void {
    console.log("data", this.data);
    this.ensureContentTypeSet();
    if (typeof this.data.stringId === "string") {
      this.data.stringId = this.data.stringId.trim().replace(/\s+/g, "_");
    }

    const args = this.buildSubmissionArgs();
    const validation = this.productContentService.validateContentData(args);

    if (!validation.isValid) {
      const errorMessages = validation.errors.map((key) => this.translate.instant(key)).join("\n");
      alert(`${this.translate.instant("content_packages.validation_error_prefix")}\n${errorMessages}`);
      return;
    }

    if (this.uploadingFile) {
      if (this.isContentPackagesManager) {
        this.handleMultipleProductsWithFile(args);
      } else {
        this.handleFileUpload(args);
      }
    } else {
      this.handleContentSave(args);
    }
  }

  private ensureContentTypeSet(): void {
    if (this.isContentPackagesManager) {
      this.data.type = this.selectedContentType;
      this.data.DTYPE = this.selectedContentType === "firmware" ? "Firmware" : "ContentPackage";
    }
  }

  private buildSubmissionArgs(): any {
    if (this.productImageOnly) {
      return this.productContentService.buildProductImageArgs(this.data, this.productId);
    }
    if (this.sampleOnly) {
      return this.productContentService.buildProductSampleArgs(this.data, this.productId);
    }

    const args: any = {
      DTYPE: this.data.DTYPE,
      fileName: this.data.fileName,
      contentLength: this.data.contentLength,
      folder: this.data.folder,
      releaseDate: this.data.releaseDate,
      title: this.data.title,
      stringId: this.data.stringId,
      downloadSection: this.data.downloadSection,
      osRequirement: this.data.osRequirement,
      description: this.data.description,
      product_id: this.productId || undefined, // Use undefined instead of null for API compatibility
      type: this.data.type,
      serialCutin: this.data.serialCutin,
      version: this.data.version,
      beta: this.data.beta,
    };

    if (this.uploadingFile) {
      this.data.mimeType = this.uploadingFile.file.type;
    }
    if (this.data.id) {
      args.id = this.data.id;
    }
    if (this.showProductSelection && this.selectedProducts.length > 0) {
      args.selectedProductIds = this.selectedProducts.map((p) => p.id);
    }

    return args;
  }

  private validateFileLocation(): boolean {
    if (this.data.exists && this.replacingFile && this.originalStorageLocation === `${this.data.folder}/${this.data.fileName}`) {
      alert(this.translate.instant("content_packages.file_location_error"));
      return false;
    }
    return true;
  }

  private performUploadWithResult(args: any, result: { url: string; file?: any }): void {
    this.createdContentPackageItem = result.file ?? result;
    this.uploader.queue.forEach((file) => {
      file.headers = [{ name: "Content-Disposition", value: encodeFilenameForHeader(args.fileName) }];
      file.url = result.url;
      this.uploader.uploadItem(file);
    });
  }

  private handleFileUpload(args: any): void {
    if (!this.validateFileLocation()) return;

    this.uploadProgress = 1;
    this.contentService.getPresignedUploadURL(args).subscribe((result) => {
      this.performUploadWithResult(args, result);
    });
  }

  private handleMultipleProductsWithFile(args: any): void {
    if (!this.validateFileLocation()) return;

    this.loading = true;
    this.uploadProgress = 1;
    this._multiProductFileUpload = true;

    const contentData = this.productContentService.buildContentData(this.data);
    const productIds = this.selectedProducts.length > 0 ? this.selectedProducts.map((p) => p.id) : this.productId ? [this.productId] : [];

    const { removed } = this.productContentService.getProductAssociationChanges(this._initialSelectedProductIds, productIds);

    this.productContentService.saveForMultipleProducts(contentData, productIds, removed).subscribe({
      next: (res: any) => {
        if (res?.url && this.uploader.queue.length) {
          this.performUploadWithResult(args, res);
        } else {
          this._multiProductFileUpload = false;
          this.loading = false;
          this.completed.next({ id: this.data.id, ...args, ...res });
          this.activeModal.close();
        }
      },
      error: (error) => {
        this._multiProductFileUpload = false;
        this.loading = false;
        this.productContentService.handleError(error, "saving content package");
      },
    });
  }

  private handleContentSave(args: any): void {
    this.loading = true;

    if (this.isContentPackagesManager) {
      this.saveForMultipleProducts(args);
    } else {
      this.saveForSingleProduct(args);
    }
  }

  private saveForMultipleProducts(args: any): void {
    const contentData = this.productContentService.buildContentData(this.data);
    const productIds = this.selectedProducts.length > 0 ? this.selectedProducts.map((p) => p.id) : this.productId ? [this.productId] : [];

    const { removed } = this.productContentService.getProductAssociationChanges(this._initialSelectedProductIds, productIds);

    this.productContentService.saveForMultipleProducts(contentData, productIds, removed).subscribe({
      next: (res: any) => {
        this.loading = false;
        this.completed.next({ id: this.data.id, ...args, ...res });
        this.activeModal.close();
      },
      error: (error) => {
        this.loading = false;
        this.productContentService.handleError(error, "saving content package");
      },
    });
  }

  private saveForSingleProduct(args: any): void {
    this.productContentService.saveForSingleProduct(args).subscribe({
      next: (res: any) => {
        this.loading = false;
        this.completed.next({ id: this.data.id, ...args });
        this.activeModal.close();
      },
      error: (error) => {
        this.loading = false;
        this.productContentService.handleError(error, "saving content package");
      },
    });
  }
}
